/**
 * Achievements module — unlock, setProgress, list.
 *
 * Writes go through Edge Functions (centralized validation,
 * idempotency guarantees, monotonic-progress enforcement). Reads
 * compose two DB queries (definitions + per-player progress) and
 * merge them client-side.
 */
import type { AchievementProgress, UnlockResult, ProgressResult } from './types';
import { VibecadeError, VibecadeErrorCode } from './types';
import type { SdkContext } from './internal/context';
import { invokeEdge } from './internal/edgeFunctions';
import { wrapError } from './internal/errors';
import { postToHost } from './internal/host';
import { withReadTimeout } from './internal/readTimeout';

interface RawAchievement {
  id: string;
  display_name: string;
  description: string | null;
  icon_url: string | null;
  points: number;
  is_progressive: boolean;
  target_progress: number | null;
}

interface RawProgress {
  achievement_id: string;
  progress: number;
  unlocked_at: string | null;
}

export class AchievementsModule {
  // Lazy-loaded display-name lookup. Populated on the first
  // unlock/progress call that actually triggers a host notification,
  // then reused for every subsequent one in the SDK's lifetime.
  // `null` = not yet loaded; empty Map = loaded but the fetch failed
  // (we still notify, just with the achievement id as the displayName).
  private definitionsCache: Map<string, RawAchievement> | null = null;

  constructor(private readonly ctx: SdkContext) {}

  async unlock(achievementId: string): Promise<UnlockResult> {
    this.requireId(achievementId);
    const result = await invokeEdge<UnlockResult>(this.ctx, 'unlock-achievement', {
      gameId: this.ctx.config.gameId,
      apiKey: this.ctx.config.apiKey,
      achievementId,
    });
    // Only fire the host toast on a fresh unlock; duplicate calls
    // (already-unlocked) shouldn't pop a toast.
    if (result.newlyUnlocked) {
      void this.notifyHostOfUnlock(achievementId);
    }
    return result;
  }

  async setProgress(achievementId: string, progress: number): Promise<ProgressResult> {
    this.requireId(achievementId);
    if (typeof progress !== 'number' || !Number.isFinite(progress) || progress < 0) {
      throw new VibecadeError(
        VibecadeErrorCode.InvalidBody,
        'setAchievementProgress: progress must be a non-negative number',
      );
    }
    const result = await invokeEdge<ProgressResult>(this.ctx, 'set-achievement-progress', {
      gameId: this.ctx.config.gameId,
      apiKey: this.ctx.config.apiKey,
      achievementId,
      progress: Math.floor(progress),
    });
    // Progressive achievements unlock when progress crosses target
    // — `newlyUnlocked` flips on exactly one call. Toast on that call
    // only.
    if (result.newlyUnlocked) {
      void this.notifyHostOfUnlock(achievementId);
    }
    return result;
  }

  /**
   * Post `{ type: 'achievement_unlocked', achievementId, displayName }`
   * to `window.parent`. Lazy-loads achievement definitions on the
   * first call so the toast can show the human-readable name. Falls
   * back to the achievement id as displayName if the lookup fails or
   * the id isn't in the catalog (shouldn't happen but defensive).
   *
   * Short-circuits before any work when `autoHostMessages` is off so
   * the catalog fetch doesn't run for opted-out consumers.
   */
  private async notifyHostOfUnlock(achievementId: string): Promise<void> {
    if (!this.ctx.config.autoHostMessages) return;
    if (typeof window === 'undefined' || window.parent === window) return;
    if (!this.definitionsCache) {
      try {
        const defs = await this.fetchDefinitions();
        this.definitionsCache = new Map(defs.map((d) => [d.id, d]));
      } catch {
        // Couldn't load — proceed without a name; cache an empty map
        // so we don't retry on every subsequent unlock.
        this.definitionsCache = new Map();
      }
    }
    const def = this.definitionsCache.get(achievementId);
    postToHost(
      {
        type: 'achievement_unlocked',
        achievementId,
        displayName: def?.display_name ?? achievementId,
        source: 'vibecade-sdk',
      },
      this.ctx.config.autoHostMessages,
    );
  }

  /**
   * Returns every achievement defined for the game, with the current
   * player's progress (zero + unlocked=false for untouched ones). Two
   * parallel DB queries: one for definitions, one for the player's
   * progress rows (RLS ensures only their own come back).
   */
  async list(): Promise<AchievementProgress[]> {
    try {
      const [defs, progress] = await Promise.all([
        this.fetchDefinitions(),
        this.fetchProgress(),
      ]);
      const progressById = new Map(progress.map((p) => [p.achievement_id, p]));

      return defs.map((d) => {
        const p = progressById.get(d.id);
        return {
          id: d.id,
          displayName: d.display_name,
          description: d.description,
          iconUrl: d.icon_url,
          points: d.points,
          isProgressive: d.is_progressive,
          targetProgress: d.target_progress,
          progress: p?.progress ?? 0,
          unlocked: !!p?.unlocked_at,
          unlockedAt: p?.unlocked_at ?? null,
        };
      });
    } catch (e) {
      throw wrapError(e, VibecadeErrorCode.AchievementsFailed, 'Could not load achievements');
    }
  }

  // -------------------------------------------------------------------------
  // private
  // -------------------------------------------------------------------------

  private async fetchDefinitions(): Promise<RawAchievement[]> {
    const { data, error } = await withReadTimeout('achievements catalog read', (signal) =>
      this.ctx.db
        .from('achievements')
        .select('id, display_name, description, icon_url, points, is_progressive, target_progress')
        .eq('game_id', this.ctx.config.gameId)
        .abortSignal(signal),
    );
    if (error) throw new VibecadeError(VibecadeErrorCode.AchievementsFailed, error.message);
    return (data ?? []) as RawAchievement[];
  }

  private async fetchProgress(): Promise<RawProgress[]> {
    // RLS restricts this to auth.uid() = player_id, so we don't need a
    // manual player_id filter — but eq()ing it is cheap belt-and-suspenders.
    const playerId = this.ctx.player.current?.id;
    let q = this.ctx.db
      .from('achievement_progress')
      .select('achievement_id, progress, unlocked_at')
      .eq('game_id', this.ctx.config.gameId);
    if (playerId) q = q.eq('player_id', playerId);

    const { data, error } = await withReadTimeout('achievement progress read', (signal) =>
      q.abortSignal(signal),
    );
    if (error) throw new VibecadeError(VibecadeErrorCode.AchievementsFailed, error.message);
    return (data ?? []) as RawProgress[];
  }

  private requireId(achievementId: unknown): asserts achievementId is string {
    if (typeof achievementId !== 'string' || !achievementId) {
      throw new VibecadeError(
        VibecadeErrorCode.InvalidBody,
        'achievementId must be a non-empty string',
      );
    }
  }
}
